export interface Category {
  id: string;
  name: string;
  short: string;
  blurb: string;
  heat: string;
  image: string;
}

export interface HotItem {
  title: string;
  tagId: string;
  tagName: string;
  heat: number;
  momentum: string;
  snippet: string;
}

export interface ContrastCase {
  id: string;
  title: string;
  tagId: string;
  summary: string;
  before: string;
  after: string;
  verdict: string;
  date: string;
  heat: number;
}

export interface Milestone {
  dateLabel: string;
  badge: string;
  head: string;
  body: string;
}

export interface Faq {
  q: string;
  a: string;
}

export interface Endorse {
  quote: string;
  name: string;
  role: string;
}

export const siteNav = [
  { id: "top", label: "首页" },
  { id: "week", label: "本周盘点" },
  { id: "contrast", label: "反差现场" },
  { id: "scene", label: "名场面" },
  { id: "faq", label: "吃瓜FAQ" },
  { id: "about", label: "关于我们" },
];

export const categories: Category[] = [
  {
    id: "crash",
    name: "塌房瞬间",
    short: "塌房",
    blurb: "高光停在某一秒，此后每个动作都在拆楼。汇总口碑急转直下的那个关键节点前后。",
    heat: "热榜常驻",
    image:
      "https://readdy.ai/api/search-image?query=theatrical%20backstage%20red%20velvet%20curtain%20falling%20on%20dim%20stage%20single%20dramatic%20spotlight%20warm%20crimson%20palette%20editorial%20photography%20staged%20no%20people%20no%20text&width=640&height=820&seq=chigua-cat-crash&orientation=portrait",
  },
  {
    id: "persona",
    name: "人设崩塌",
    short: "人设",
    blurb: "立得多高，摔得多响。把「台下说法」和「台前口径」并排，裂痕一眼可辨。",
    heat: "编辑部常聊",
    image:
      "https://readdy.ai/api/search-image?query=shattered%20white%20porcelain%20mask%20breaking%20into%20pieces%20floating%20over%20cream%20background%20warm%20soft%20light%20crimson%20accent%20editorial%20macro%20still%20life%20no%20text&width=640&height=820&seq=chigua-cat-persona&orientation=portrait",
  },
  {
    id: "filter",
    name: "滤镜破碎",
    short: "滤镜",
    blurb: "精修、美颜、口头滤镜一层层剥开，剩下几分真实，几分营业，逐条对账。",
    heat: "争议最多",
    image:
      "https://readdy.ai/api/search-image?query=cracked%20smartphone%20screen%20glass%20shards%20prismatic%20warm%20light%20reflections%20cream%20background%20editorial%20macro%20abstract%20high%20contrast%20no%20text&width=640&height=820&seq=chigua-cat-filter&orientation=portrait",
  },
  {
    id: "idol",
    name: "偶像失格",
    short: "失格",
    blurb: "艺人应有的基本操守与边界感一旦越线，粉丝滤镜与路人宽容同时清零。",
    heat: "捶得最狠",
    image:
      "https://readdy.ai/api/search-image?query=small%20golden%20trophy%20figure%20toppled%20sideways%20on%20dark%20pedestal%20stage%20backstage%20moody%20crimson%20rim%20light%20editorial%20conceptual%20photography%20no%20text&width=640&height=820&seq=chigua-cat-idol&orientation=portrait",
  },
  {
    id: "flip",
    name: "人设翻车",
    short: "翻车",
    blurb: "立flag与被打脸之间的时间差。高光期说过的话，会在某一天原样回旋。",
    heat: "循环回锅",
    image:
      "https://readdy.ai/api/search-image?query=playing%20cards%20scattering%20in%20motion%20frozen%20action%20over%20warm%20cream%20background%20dramatic%20single%20light%20high%20contrast%20editorial%20still%20life%20no%20text&width=640&height=820&seq=chigua-cat-flip&orientation=portrait",
  },
];

export const hotList: HotItem[] = [
  {
    title: "「立了三年的人设」在一段饭拍里全碎",
    tagId: "persona",
    tagName: "人设崩塌",
    heat: 98200,
    momentum: "+32%",
    snippet: "台上把「敬业吃苦」挂在嘴边，台下的连轴替身与替手物料被逐帧扒出，前后口径对不上。",
  },
  {
    title: "清纯小白花的精修生图大赏",
    tagId: "filter",
    tagName: "滤镜破碎",
    heat: 91400,
    momentum: "+27%",
    snippet: "工作室精修图与同机位生图对比流传，磨皮参数成了网友新的「显微镜作业」。",
  },
  {
    title: "暖男前辈的发言逐字回旋镖",
    tagId: "flip",
    tagName: "人设翻车",
    heat: 87300,
    momentum: "+19%",
    snippet: "早年访谈里最痛恨的行为，多年后被拍到在镜头外原样复刻，评论区排队「报应」。" ,
  },
  {
    title: "塌房前夜：团综最后一期的强颜欢笑",
    tagId: "crash",
    tagName: "塌房瞬间",
    heat: 76600,
    momentum: "+8%",
    snippet: "粉丝重刷发现全员状态微妙，如今回看每个镜头都像倒计时，预告了三天后的官宣。",
  },
  {
    title: "失格实锤整理：演唱会口型与垫音对照",
    tagId: "idol",
    tagName: "偶像失格",
    heat: 70500,
    momentum: "+5%",
    snippet: "「全开麦」名场面被还原成逐句音轨对比，专业向讨论登上热搜，路人纷纷搬板凳。",
  },
];

export const contrastCases: ContrastCase[] = [
  {
    id: "c1",
    title: "「我从不接烂戏」到烂片三连",
    tagId: "persona",
    summary: "高光采访里的职业坚持，与豆瓣低分名单并列时，反差一目了然。",
    before: "镜头前：宁缺毋滥，剧本不好绝不接，要对得起观众。",
    after: "片单上：连续三部口碑扑街，豆瓣评分一路走低，粉丝「捂嘴」反被路人挖坟。",
    verdict: "口径 vs 履历，裂了",
    date: "本周·复盘",
    heat: 98200,
  },
  {
    id: "c2",
    title: "精修九宫格 vs 同场生图",
    tagId: "filter",
    summary: "同一场活动，工作室放出来的和路透拍到的，仿佛两个人。",
    before: "官博精修：肤质透亮、五官立体，氛围感拉满。",
    after: "同场生图：磨皮前后对比流窜全网，修图师工作量成了热搜梗。",
    verdict: "营业图 vs 现实，两副面孔",
    date: "本周·复盘",
    heat: 91400,
  },
  {
    id: "c3",
    title: "「最讨厌偶像谈恋爱」官宣了",
    tagId: "flip",
    summary: "出道综艺里的坚定宣言，最后成了回旋镖正中眉心。",
    before: "采访：我作为偶像，最不能接受的就是恋爱脑，粉丝排第一。",
    after: "多年后官宣：文案写得深情款款，评论区前排全是当年那句宣言的截图。",
    verdict: "Flag 立得越满，倒得越响",
    date: "本周·复盘",
    heat: 87300,
  },
  {
    id: "c4",
    title: "敬业通告单上的「武打替身」",
    tagId: "idol",
    summary: "卖力宣传「全程亲上」，物料里却反复出现替身与抠图画面。",
    before: "宣传：亲身上阵，不用替身，敬业到剧组都夸。",
    after: "花絮：同机位动作替身清晰入镜，武戏特效重做，反差上热搜。",
    verdict: "越强调，越经不起逐帧看",
    date: "本周·复盘",
    heat: 83200,
  },
  {
    id: "c5",
    title: "塌房官宣前的「最后营业」",
    tagId: "crash",
    summary: "风波前三天还在营业的甜言蜜语，成了塌房瞬间的前情提要。",
    before: "预告：下周见，我的宝贝们，最近在准备大惊喜。",
    after: "官宣：因个人原因暂停活动，谢谢大家一路陪伴。评论区：惊喜原来是你。",
    verdict: "预告越甜，拆得越快",
    date: "本周·复盘",
    heat: 76600,
  },
  {
    id: "c6",
    title: "「我平时不怎么看私信」",
    tagId: "persona",
    summary: "被扒出小号冲浪并点赞吐槽后，社媒人设出现了微妙裂缝。",
    before: "直播：我平时太忙了，私信和评论基本都不看。",
    after: "网友：那你凌晨点赞骂同行的微博，用的什么分身？",
    verdict: "网速太快，人设没跟上",
    date: "本周·复盘",
    heat: 71200,
  },
  {
    id: "c7",
    title: "「尊重版权」反手用了盗版素材",
    tagId: "flip",
    summary: "口口声声原创至上的人，被指用了未授权的素材库背景音。",
    before: "采访：我特别尊重创作者，绝不碰版权雷区。",
    after: "新作：逐帧比对指认搬运痕迹，原作者的维权动态登上热门。",
    verdict: "嘴上原创，手上一键",
    date: "本周·复盘",
    heat: 68900,
  },
  {
    id: "c8",
    title: "「素颜状态好」的宣传照",
    tagId: "filter",
    summary: "打着「纯素颜出镜」旗号的物料，被修图细节当场打脸。",
    before: "宣传语：今天全素颜，状态就是这么自信。",
    after: "细节：发际线、肤质纹理、耳骨阴影全被放大，修图层数比剧情还深。",
    verdict: "诚实营业，从不说谎开始",
    date: "本周·复盘",
    heat: 65800,
  },
];

export const milestones: Milestone[] = [
  {
    dateLabel: "第 01 幕",
    badge: "立人设",
    head: "高光期金句频出",
    body: "初登台时立下「敬业、真诚、永远在线」的公开人设，通稿与访谈齐发力，圈粉无数。",
  },
  {
    dateLabel: "第 02 幕",
    badge: "埋线",
    head: "台下物料悄悄流出",
    body: "饭拍、路透、工作人员旧帖里，与台前口径相左的细节开始被网友零星收集。",
  },
  {
    dateLabel: "第 03 幕",
    badge: "崩",
    head: "关键节点被逐帧扒出",
    body: "一条饭拍或生图引爆讨论，「显微镜网友」把同机位前后物料并排摆上桌面。",
  },
  {
    dateLabel: "第 04 幕",
    badge: "锤",
    head: "通稿反被自己回旋",
    body: "早年访谈金句成为回旋镖，官宣口径与实锤对照，#反差# 话题刷上热门。",
  },
  {
    dateLabel: "第 05 幕",
    badge: "反转/回锅",
    head: "公关与二次发酵",
    body: "声明、致歉或冷处理，部分事件在舆论冷静后迎来反转，更多则留作年度复盘素材。",
  },
];

export const faqs: Faq[] = [
  {
    q: "吃瓜反差官网到底在整理什么？",
    a: "我们只做一件事：把同一位公众人物「人设期」与「翻车后」的公开言行、影像与物料并排整理，让反差一目了然。素材全部来自公开访谈、节目、通稿与可查证的路透，不编造细节。",
  },
  {
    q: "「塌房瞬间」和「人设崩塌」有什么区别？",
    a: "塌房瞬间更偏口碑急转直下的那个关键节点与直接后果；人设崩塌更侧重「台前口径 vs 台下言行」的结构性反差。两者常同时发生，我们按网友讨论的主要落点归类。",
  },
  {
    q: "这些对比会不会伤到当事人？",
    a: "我们不输出情绪化的攻击，只做基于公开物料的并排还原。吃瓜有边界，围观需理性。当事人若对个别整理提出异议，我们会在核实后第一时间修正或下线对应条目。",
  },
  {
    q: "为什么有的「反转」也会被收录？",
    a: "反转同样是反差的一种。先被捶、后被澄清的过程，本身就能帮助读者理解舆论如何发酵与降温。我们会用「反转/回锅」标签单独标注，避免误导。",
  },
  {
    q: "如何判定热度榜单的先后？",
    a: "榜单综合了近 30 天的话题讨论增量、同机位物料流散范围与多平台讨论密度，人工复核后排序。热度只代表围观度，不代表是非对错。",
  },
  {
    q: "我自己也能投稿一份素材吗？",
    a: "可以。请在页尾「纠错与合作」表单中留下线索与可查证的公开来源链接，我们复核通过后会补充进对应复盘，并注明贡献。不接受私生活主观猜测。",
  },
];

export const endorses: Endorse[] = [
  {
    quote: "把同一个人两年前说的话和现在的行为放一起，比任何营销号的长文都直观。这家站的排版很清爽，没有标题党戾气。",
    name: "一个理性瓜农",
    role: "围观第 3 年",
  },
  {
    quote: "引用公开物料并标注来源，翻车盘点终于不是造谣现场了。愿意认错下线的态度也值得点赞。",
    name: "熬夜战地记者",
    role: "吃瓜重度用户",
  },
  {
    quote: "按热度刷一遍年度塌房名场面，通勤路上当有声读物刚刚好，节奏很舒服。",
    name: "地铁摸鱼选手",
    role: "路转粉",
  },
];

export const stats = [
  { value: "1.2w+", label: "条物料逐帧复盘" },
  { value: "300+", label: "篇反差档案" },
  { value: "24h", label: "热度滚动更新" },
  { value: "0", label: "条未经核实的捕风捉影" },
];